/**
 * storage.ts — Low-level localStorage read/write for the unified DemoState.
 *
 * All access to localStorage goes through this module.
 * Components and services MUST NOT access localStorage directly.
 */

import type { DemoState } from './schema';
import { DEMO_STATE_KEY, DEMO_STATE_BACKUP_PREFIX } from './schema';

// ─── Core read/write ───────────────────────────────────────────────────

export function readRawState(): unknown | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(DEMO_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeRawState(state: DemoState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DEMO_STATE_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded or private mode — silently ignore
  }
}

export function clearRawState(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(DEMO_STATE_KEY);
  } catch {
    // ignore
  }
}

// ─── Backup ────────────────────────────────────────────────────────────

export function backupCurrentState(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(DEMO_STATE_KEY);
    if (!raw) return null;
    const key = `${DEMO_STATE_BACKUP_PREFIX}${Date.now()}`;
    localStorage.setItem(key, raw);
    return key;
  } catch {
    return null;
  }
}

// ─── Legacy key cleanup ────────────────────────────────────────────────

const LEGACY_KEYS = [
  'demo_sessions',
  'demo_agenda_items',
  'demo_documents',
  'demo_drafts',
  'demo_issues',
  'demo_suggested_questions',
  'demo_item_notes',
  'demo_item_responsables',
];

export function removeLegacyKeys(): void {
  if (typeof window === 'undefined') return;
  LEGACY_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });
}

export function hasLegacyKeys(): boolean {
  if (typeof window === 'undefined') return false;
  return LEGACY_KEYS.some((k) => {
    try {
      return localStorage.getItem(k) !== null;
    } catch {
      return false;
    }
  });
}
